/**
 * ProductThumb — v149.70.
 *
 * A product / service / event picture, or — when it has none, or the
 * picture fails to load — its initials on a Linkola colour:
 *
 *     Indian Orange → IO      Custard Apple → CA      Carrot → CA
 *
 * Never an empty box and never the browser's broken-image icon. Used
 * wherever a product is drawn: store tiles and cards, the cart, New order,
 * Inventory, the Bio page's product grid, the single-product page.
 *
 * `className` sizes the box and is applied to the picture and to the
 * fallback alike, so swapping one for the other never moves the layout.
 */
import { useEffect, useState } from "react";
import { sizedImage } from "../../lib/imageUrl";

// "Indian Orange" → "IO"; one word → its first two letters; nothing → "?".
export function initialsOf(name) {
  const words = String(name || "")
    .split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}\p{N}]+/u, ""))
    .filter(Boolean);
  if (words.length === 0) return "?";
  const first = Array.from(words[0]);
  if (words.length === 1) return first.slice(0, 2).join("").toUpperCase();
  return (first[0] + Array.from(words[1])[0]).toUpperCase();
}

const TONES = ["bg-mint", "bg-sun", "bg-lavender", "bg-bubble", "bg-cream"];

// Same name → same colour, on every screen and every visit.
function toneOf(name) {
  let h = 0;
  for (const ch of String(name || "")) h = (h * 31 + ch.codePointAt(0)) % 9973;
  return TONES[h % TONES.length];
}

export function ProductInitials({ name, className = "", testid }) {
  return (
    <span
      className={`${className} ${toneOf(name)} text-ink inline-flex items-center justify-center overflow-hidden select-none`}
      role="img"
      aria-label={name ? `${name} (no photo)` : "No photo"}
      data-testid={testid}
      data-initials={initialsOf(name)}
    >
      {/* Drawn as SVG so the letters scale with the box, whatever its size. */}
      <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true">
        <text
          x="50" y="52" textAnchor="middle" dominantBaseline="middle"
          fontSize="38" fill="currentColor" className="font-display font-black"
        >
          {initialsOf(name)}
        </text>
      </svg>
    </span>
  );
}

export default function ProductThumb({
  src, name, className = "", fit = "cover", width, alt = "", loading = "lazy",
  testid, fallbackTestid, onLoad, fetchPriority,
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (!src || broken) {
    return <ProductInitials name={name} className={className} testid={fallbackTestid || testid} />;
  }
  return (
    <img
      src={width ? sizedImage(src, width) : src}
      alt={alt}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      onLoad={onLoad}
      onError={() => setBroken(true)}
      className={`${className} ${fit === "contain" ? "object-contain" : "object-cover"}`}
      data-testid={testid}
    />
  );
}
